import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { Spinner } from '@quebon/ui/components/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@quebon/ui/components/table'
import { cn } from '@quebon/ui/lib/utils'

import { saveBlob } from '@/api/csvDownload'
import { lcSettlementApi, type LcSettlementParams, type LcSettlementSummaryRow } from '@/api/lcSettlement'
import { describeApiError, formatWon } from './format'
import { lcSettlementQueryKeys } from './queryKeys'

interface Props {
  params: LcSettlementParams
}

const AMOUNT_COLUMNS: Array<{ key: keyof LcSettlementSummaryRow; label: string }> = [
  { key: 'contentFeeAmount', label: '콘텐츠 사용료' },
  { key: 'textbookAmount', label: '깨봉몰 교재' },
  { key: 'nonTextbookAmount', label: '깨봉몰 교재 외' },
  { key: 'shippingAmount', label: '깨봉몰 배송비' },
  { key: 'refundAmount', label: '환불 합계' },
  { key: 'totalAmount', label: '합계' },
]

function sumBy(rows: LcSettlementSummaryRow[], key: keyof LcSettlementSummaryRow): number {
  return rows.reduce((acc, row) => acc + Number(row[key]), 0)
}

export function SummaryTab({ params }: Props) {
  const [downloading, setDownloading] = useState(false)
  const summaryQuery = useQuery({
    queryKey: lcSettlementQueryKeys.summary(params),
    queryFn: () => lcSettlementApi.getSummary(params),
    placeholderData: (previous) => previous,
  })
  const rows = summaryQuery.data ?? []

  const totals = useMemo(
    () => ({
      paymentCount: sumBy(rows, 'paymentCount'),
      refundCount: sumBy(rows, 'refundCount'),
      amounts: AMOUNT_COLUMNS.map((column) => sumBy(rows, column.key)),
    }),
    [rows],
  )

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const { blob, filename } = await lcSettlementApi.exportSummary(params)
      saveBlob(blob, filename)
    } catch (error) {
      toast.error(describeApiError(error, '요약 CSV 다운로드에 실패했습니다.'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>센터별 결제 요약</CardTitle>
        <CardDescription>
          {params.dateFrom} ~ {params.dateTo} 기간의 결제와 환불을 센터별로 합산합니다. 환불은 음수이며 각 금액은 순액입니다.
        </CardDescription>
        <CardAction>
          <Button type="button" variant="outline" onClick={handleDownload} disabled={downloading || summaryQuery.isLoading}>
            {downloading ? <Spinner data-icon="inline-start" /> : <Download data-icon="inline-start" />}
            CSV 다운로드
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="pt-4">
        {summaryQuery.isError ? (
          <Alert variant="destructive">
            <AlertTitle>요약을 불러오지 못했습니다.</AlertTitle>
            <AlertDescription>{describeApiError(summaryQuery.error, '잠시 후 다시 시도하세요.')}</AlertDescription>
          </Alert>
        ) : summaryQuery.isLoading ? (
          <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner />
            요약을 불러오는 중입니다.
          </div>
        ) : (
          <Table className={cn('min-w-[1100px]', summaryQuery.isFetching && 'opacity-70')}>
            <TableHeader>
              <TableRow>
                <TableHead>센터</TableHead>
                <TableHead className="text-right">결제 건수</TableHead>
                <TableHead className="text-right">환불 건수</TableHead>
                {AMOUNT_COLUMNS.map((column) => (
                  <TableHead key={column.key} className="text-right">{column.label} (원)</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3 + AMOUNT_COLUMNS.length} className="py-12 text-center text-muted-foreground">
                    조건에 맞는 결제 항목이 없습니다.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.organizationId}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{row.centerName}</span>
                        {row.isTestCenter ? <Badge variant="outline">테스트센터</Badge> : null}
                      </div>
                      {row.subdomain ? <div className="text-xs text-muted-foreground">{row.subdomain}</div> : null}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.paymentCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.refundCount}</TableCell>
                    {AMOUNT_COLUMNS.map((column) => (
                      <TableCell key={column.key} className="text-right tabular-nums">
                        {formatWon(Number(row[column.key]))}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
            {rows.length > 0 ? (
              <TableFooter>
                <TableRow>
                  <TableCell>합계 ({rows.length}개 센터)</TableCell>
                  <TableCell className="text-right tabular-nums">{totals.paymentCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{totals.refundCount}</TableCell>
                  {totals.amounts.map((amount, index) => (
                    <TableCell key={AMOUNT_COLUMNS[index].key} className="text-right tabular-nums">
                      {formatWon(amount)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableFooter>
            ) : null}
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
